import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@shadcn/components/ui/button";
import { Content } from "@forms/form-render/components/Fields/Content";
import { VonageVerifyEmailEntry } from "../VonageVerifyEmailEntry";
import { VonageVerifyCodeEntry } from "../VonageVerifyCodeEntry";
import { VonageVerifyInlineProps } from "./VonageVerifyInline.types";
import { enquiryApi } from "@/data/enquiries/api";
import { AlertVariant, ContentFieldConfig } from "@schema-types/types/JourneySchema";

const sentInfoConfig: ContentFieldConfig = {
  alertVariant: AlertVariant.Success,
  icon: "Mail",
  content: "Code sent. Ask the client to read out the code from the email once it arrives.",
};

const sendFailedConfig: ContentFieldConfig = {
  alertVariant: AlertVariant.Warning,
  icon: "AlertTriangle",
  content: "The code could not be sent. Check the email address and try again.",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const VonageVerifyInline = ({
  name,
  required,
  disabled,
  codeLength,
  enquiryId,
  currentStepId,
}: VonageVerifyInlineProps) => {
  const { getValues, trigger } = useFormContext();
  const canSendViaApi = Boolean(enquiryId && currentStepId);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendStatus, setSendStatus] = useState<"sent" | "failed" | null>(null);

  const handleSend = async () => {
    if (!enquiryId || !currentStepId || sendLoading) return;
    const email = getValues("email_address");
    if (!email || !EMAIL_PATTERN.test(email)) {
      await trigger("email_address");
      return;
    }
    setSendLoading(true);
    try {
      const result = await enquiryApi.sendCode({ enquiryId, currentStepId, email });
      setSendStatus(result.success ? "sent" : "failed");
    } catch {
      setSendStatus("failed");
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <VonageVerifyEmailEntry name="" required={required} disabled={disabled} showInfoMessage={false} />
      {canSendViaApi && (
        <div className="flex justify-center">
          <Button type="button" variant="outline" size="lg" onClick={handleSend} disabled={sendLoading || disabled}>
            {sendLoading ? "Sending…" : sendStatus === "sent" ? "Resend code" : "Send code"}
          </Button>
        </div>
      )}
      {sendStatus === "sent" && <Content config={sentInfoConfig} />}
      {sendStatus === "failed" && <Content config={sendFailedConfig} />}
      <VonageVerifyCodeEntry
        name={name}
        required={required}
        disabled={disabled}
        codeLength={codeLength}
        enquiryId={enquiryId}
        currentStepId={currentStepId}
      />
    </div>
  );
};
